<template>
  <v-stepper v-model="currentStep">
    <v-stepper-header>
      <v-stepper-step
        :complete="REAUTHENTICATE.complete"
        :step="REAUTHENTICATE.index"
        :rules="REAUTHENTICATE.errors"
      >
        Re-authenticate
      </v-stepper-step>
      <v-divider />
      <v-stepper-step
        :complete="PHONE_NUMBER.complete"
        :step="PHONE_NUMBER.index"
        :rules="PHONE_NUMBER.errors"
      >
        Enter phone number
      </v-stepper-step>
      <v-divider />
      <v-stepper-step :step="VERIFICATION_CODE.index">
        Enter verification code
      </v-stepper-step>
    </v-stepper-header>

    <v-stepper-items>
      <mfa-step
        :step="REAUTHENTICATE"
        :data-testid="REAUTHENTICATE.id"
      >
        <template #title>
          Re-authenticate
        </template>
        <template #subtitle>
          Enabling MFA requires you to enter your password
        </template>
        <auth-reauthenticate @reauthenticate="reauthenticateCompleted" />
      </mfa-step>

      <mfa-step
        :step="PHONE_NUMBER"
        :data-testid="PHONE_NUMBER.id"
      >
        <template #title>
          Enter your phone number
        </template>
        <template #subtitle>
          This is the phone number which will receive your verification codes by
          SMS
        </template>
        <enable-mfa-phone-number
          @phoneNumberVerified="phoneNumberCompleted"
          @error="handleError"
        />
      </mfa-step>

      <mfa-step
        :step="VERIFICATION_CODE"
        :data-testid="VERIFICATION_CODE.id"
      >
        <template #title>
          Enter verification code
        </template>
        <template #subtitle>
          Please enter the verification code you received by SMS to your
          nominated phone number
        </template>
        <enable-mfa-verification-code
          :verification-id="verificationId"
          @mfaEnabled="verificationCodeCompleted"
          @error="handleError"
        />
      </mfa-step>
    </v-stepper-items>
  </v-stepper>
</template>

<script>
import MfaStep from '../MfaStep.vue'
import EnableMfaPhoneNumber from './EnableMfaPhoneNumber.vue'
import EnableMfaVerificationCode from './EnableMfaVerificationCode.vue'
import AuthReauthenticate from '../../AuthReauthenticate.vue'
import { createStepData } from '@/utils/steppers'
import { logMfaEnabled } from '@/analytics'
import {
  AUTH_CODE_EXPIRED,
  AUTH_CAPTCHA_EXPIRED,
  AUTH_INVALID_PHONE_NUMBER,
  AUTH_INVALID_VERIFICATION_CODE,
  AUTH_REQUIRES_RECENT_LOGIN,
  AUTH_SECOND_FACTOR_ALREADY_IN_USE,
  AUTH_TOO_MANY_REQUESTS,
  AUTH_USER_TOKEN_EXPIRED
} from '@/utils/auth'

const REAUTHENTICATE = 'REAUTHENTICATE'
const PHONE_NUMBER = 'PHONE_NUMBER'
const VERIFICATION_CODE = 'VERIFICATION_CODE'

export default {
  name: 'EnableMfa',
  components: {
    AuthReauthenticate,
    EnableMfaPhoneNumber,
    MfaStep,
    EnableMfaVerificationCode
  },
  data: function () {
    return {
      currentStep: 1,
      verificationId: null,
      ...createStepData(REAUTHENTICATE, PHONE_NUMBER, VERIFICATION_CODE)
    }
  },
  computed: {},
  methods: {
    async reauthenticateCompleted() {
      this.completeStep(REAUTHENTICATE)
      this.setCurrentStep(PHONE_NUMBER)
    },
    phoneNumberCompleted(verificationId) {
      this.verificationId = verificationId
      this.completeStep(PHONE_NUMBER)
      this.setCurrentStep(VERIFICATION_CODE)
    },
    async verificationCodeCompleted() {
      await this.$store.dispatch('reloadCurrentUser')
      logMfaEnabled()
    },
    handleError(error) {
      switch (error.code) {
      case AUTH_USER_TOKEN_EXPIRED:
      case AUTH_REQUIRES_RECENT_LOGIN:
        this.addErrorMessage(
          REAUTHENTICATE,
          'Your credentials have expired. You will need to sign in again.'
        )
        break
      case AUTH_CODE_EXPIRED:
        this.addErrorMessage(
          PHONE_NUMBER,
          'Your verification code has expired. You will have to re-confirm your phone number.'
        )
        break
      case AUTH_CAPTCHA_EXPIRED:
        if (!this.verificationId) {
          this.addErrorMessage(
            PHONE_NUMBER,
            'The reCAPTCHA has expired. You will have to complete it again.'
          )
        }
        break
      case AUTH_INVALID_PHONE_NUMBER:
        this.addErrorMessage(
          PHONE_NUMBER,
          'Not a valid phone number. Your phone number must be in international format.'
        )
        break
      case AUTH_SECOND_FACTOR_ALREADY_IN_USE:
        this.addErrorMessage(
          PHONE_NUMBER,
          'That phone number is already enrolled as a second factor for this account.'
        )
        break
      case AUTH_INVALID_VERIFICATION_CODE:
        this.addErrorMessage(VERIFICATION_CODE, 'Invalid verification code.')
        break
      case AUTH_TOO_MANY_REQUESTS:
        this.addErrorMessage(
          REAUTHENTICATE,
          'Requests from this device have been blocked due to unusual activity. Try again later.'
        )
        break
      default:
        throw error
      }
    },
    addErrorMessage(stepId, message) {
      this.step(stepId).errors = [() => message]
      this.setCurrentStep(stepId)
    },
    completeStep(stepId) {
      const step = this.step(stepId)
      step.complete = true
      step.errors = []
    },
    setCurrentStep(stepId) {
      this.currentStep = this.step(stepId).index
    },
    step(stepId) {
      return this[stepId]
    }
  }
}
</script>
